import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight,
  Lightbulb,
  BarChart3,
  Shield,
  Globe,
  Clock,
  Award,
  MessageSquare,
  Settings,
  Zap
} from 'lucide-react';

const ConsultingServices: React.FC = () => {
  const consultingAreas = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Business Strategy",
      description: "Develop comprehensive business strategies to achieve your organizational goals and drive growth.",
      services: ["Market analysis", "Competitive positioning", "Growth strategy", "Business model optimization"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Technology Strategy",
      description: "Align your technology investments with business objectives for maximum ROI and efficiency.",
      services: ["Technology roadmap", "Digital transformation", "System architecture", "Integration strategy"]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Change Management",
      description: "Guide your organization through successful digital transformation and process improvements.",
      services: ["Stakeholder engagement", "Training programs", "Communication strategy", "Performance monitoring"]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Risk Management",
      description: "Identify and mitigate risks while ensuring compliance with industry standards and regulations.",
      services: ["Risk assessment", "Compliance frameworks", "Security audits", "Business continuity"]
    }
  ];

  const benefits = [
    "Expert guidance from industry professionals",
    "Customized solutions for your unique needs",
    "Proven methodologies and best practices",
    "Measurable outcomes and ROI tracking",
    "Ongoing support and follow-up services",
    "Access to latest industry insights and trends"
  ];

  const consultingProcess = [
    {
      step: "01",
      title: "Discovery & Assessment",
      description: "We conduct a thorough analysis of your current state, challenges, and objectives."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive strategy tailored to your business needs and goals."
    },
    {
      step: "03",
      title: "Implementation Planning",
      description: "Develop detailed implementation plans with timelines, resources, and milestones."
    },
    {
      step: "04",
      title: "Execution Support",
      description: "Provide hands-on support during implementation to ensure successful delivery."
    },
    {
      step: "05",
      title: "Monitoring & Optimization",
      description: "Track progress, measure outcomes, and optimize strategies for continuous improvement."
    },
    {
      step: "06",
      title: "Knowledge Transfer",
      description: "Ensure your team has the skills and knowledge to sustain improvements long-term."
    }
  ];

  const expertiseAreas = [
    {
      title: "Microsoft Dynamics 365",
      description: "Expert guidance on Dynamics 365 implementation, customization, and optimization.",
      icon: <Settings className="w-8 h-8" />
    },
    {
      title: "Power Platform",
      description: "Strategic consulting for Power Apps, Power BI, Power Automate, and Power Virtual Agents.",
      icon: <Zap className="w-8 h-8" />
    },
    {
      title: "Digital Transformation",
      description: "End-to-end digital transformation consulting to modernize your business operations.",
      icon: <Globe className="w-8 h-8" />
    },
    {
      title: "Business Process Optimization",
      description: "Streamline operations and improve efficiency through process analysis and redesign.",
      icon: <TrendingUp className="w-8 h-8" />
    }
  ];

  const testimonials = [
    {
      quote: "The consulting team helped us transform our business operations and achieve 40% efficiency gains.",
      author: "Sarah Johnson",
      position: "CEO, TechCorp Solutions",
      company: "TechCorp Solutions"
    },
    {
      quote: "Their strategic guidance was invaluable in our digital transformation journey.",
      author: "Michael Chen",
      position: "CTO, InnovateTech",
      company: "InnovateTech"
    },
    {
      quote: "Professional, knowledgeable, and results-driven consulting services.",
      author: "Emily Rodriguez",
      position: "Operations Director",
      company: "Global Enterprises"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
             {/* Hero Section */}
       <section className="pt-48 pb-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Users className="w-12 h-12 mr-4" />
              <h1 className="text-5xl font-bold">Consulting Services</h1>
            </div>
            <p className="text-xl text-indigo-100 mb-8">
              Expert guidance to transform your business. Our consulting services help organizations 
              navigate digital transformation, optimize operations, and achieve sustainable growth.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-all duration-300"
            >
              Schedule Consultation
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Consulting Areas */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Consulting Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive consulting services across multiple domains to help your 
              organization achieve its strategic objectives and drive sustainable growth.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {consultingAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-indigo-600 mb-4">{area.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  {area.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {area.description}
                </p>
                <div className="space-y-2">
                  {area.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Consulting?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We bring deep expertise, proven methodologies, and a results-driven approach to every engagement.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Areas of Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our consultants specialize in key technology areas and business domains to provide 
              targeted, effective solutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertiseAreas.map((expertise, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-indigo-600 mb-4 flex justify-center">{expertise.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {expertise.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {expertise.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Process */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Consulting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a structured, proven methodology to ensure successful outcomes and maximum value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {step.title}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from organizations that have transformed their business with our consulting services.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <MessageSquare className="w-6 h-6 text-indigo-600 mr-2" />
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                  <p className="text-sm text-indigo-600">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Let our expert consultants help you navigate challenges, optimize operations, and achieve 
              your strategic goals. Start with a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-all duration-300"
              >
                Schedule Consultation
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-indigo-600 transition-all duration-300"
              >
                Download Brochure
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ConsultingServices; 